import React, { ReactElement, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { SelectField, TextInputField } from '@bucketplace/commerce-admin-ui';

import { FormContent, InspectorFormProvider, LogDataFormGroup } from '../../components';
import { useLinkContext } from '../../contexts';
import { DocumentInspectorProps, DocumentNode } from '../../types';
import { LinkProps } from './Link';

const OPEN_OPTIONS = [
  {
    value: 'sameTab',
    text: '현재 창',
  },
  {
    value: 'newTab',
    text: '새 창',
  },
  {
    value: 'anchor',
    text: '앵커',
  },
];

export function LinkInspector({
  value,
  onChange,
}: DocumentInspectorProps<LinkProps>) {
  return (
    <InspectorFormProvider value={value} onChange={onChange}>
      <LinkInspectorFields value={value} />
    </InspectorFormProvider>
  );
}

interface LinkInspectorFieldStrings {
  to: string;
  openOption: string;
  logData: string;
}

export function LinkInspectorFields({
  names: customNames,
  labels: customLabels,
  value,
}: {
  names?: Partial<LinkInspectorFieldStrings>,
  labels?: Partial<LinkInspectorFieldStrings>,
  value?: DocumentNode<LinkProps>,
}) {
  const { setValue } = useFormContext();
  const { style } = value?.props ?? {};
  const names = {
    ...{
      to: 'to',
      openOption: 'openOption',
      logData: 'logData',
    },
    ...customNames,
  };

  const labels = {
    ...{
      to: 'URL',
      openOption: '이동 방식',
      logData: '로그',
    },
    ...customLabels,
  };

  useEffect(() => {
    setValue('style', style);
  }, [style, setValue]);

  return (
    <>
      <FormContent
        header={labels.to}
        required
        direction="VERTICAL"
      >
        <TextInputField
          name={names.to}
          width="100%"
          rules={{
            required: true,
            validate: (value) => value.trim() !== '',
          }}
        />
      </FormContent>
      <FormContent header={labels.openOption}>
        <SelectField
          name={names.openOption}
          width="100%"
          options={OPEN_OPTIONS}
        />
      </FormContent>
      <LogDataInput value={value!} />
    </>
  );
}

const LogDataInput = React.memo(({ value }: { value: DocumentNode<LinkProps> }): ReactElement => {
  const { setLogData } = useLinkContext();
  const { children, ...others } = value ?? {};

  return (
    <LogDataFormGroup
      nodeKey={value?.key}
      {...setLogData?.(others)}
    />
  );
});
